import {
  ArrowsClockwise,
  CloudCheck,
  CloudSlash,
  MagnifyingGlass,
  List,
} from '@phosphor-icons/react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { cx } from '@/components/ui/cx';
import { useWide } from '@/hooks/useMediaQuery';
import { useTheme } from '@/hooks/usePreferences';
import { fmtAgo } from '@/lib/format';
import { useRealtimeStatus } from '@/lib/realtime';

import { CommandPalette } from './CommandPalette';
import { LanguageMenu, ThemeToggleButton } from './LanguageMenu';
import { NotificationsPopover } from './NotificationsPopover';

function SyncPill({ wide }: { wide: boolean }) {
  const { t } = useTranslation();
  const { status, lastEventAt } = useRealtimeStatus();
  const [, force] = useState(0);
  useEffect(() => {
    const id = setInterval(() => force((x) => x + 1), 30_000);
    return () => clearInterval(id);
  }, []);
  const connected = status === 'connected';
  const offline = status === 'offline';
  const label = connected
    ? lastEventAt
      ? t('panel:shell.sync.syncedAgo', {
          time: fmtAgo(new Date(lastEventAt).toISOString(), t).toLowerCase(),
        })
      : t('panel:shell.sync.synced')
    : offline
      ? t('panel:shell.sync.offline')
      : status === 'connecting'
        ? t('panel:shell.sync.syncing')
        : t('panel:shell.sync.reconnecting');
  const Icon = connected ? CloudCheck : offline ? CloudSlash : ArrowsClockwise;
  return (
    <span
      role="status"
      title={label}
      aria-label={label}
      data-testid="realtime-status"
      data-status={status}
      className={cx(
        'inline-flex h-[34px] flex-none items-center gap-2 rounded-full px-2.5 text-[13px] font-medium whitespace-nowrap',
        connected
          ? 'bg-ok-bg text-ok'
          : offline
            ? 'bg-danger-bg text-danger'
            : 'bg-caution-bg text-caution',
      )}
    >
      <Icon
        size={16}
        className={cx(!connected && !offline && 'animate-spin [animation-duration:2.4s]')}
      />
      {wide ? label : null}
    </span>
  );
}

export function Header({
  onOpenMenu,
  showMenuButton,
  canSettings,
}: {
  onOpenMenu: () => void;
  showMenuButton: boolean;
  canSettings: boolean;
}) {
  const { t } = useTranslation();
  const wide = useWide();
  const { theme, toggle } = useTheme(true);
  const [paletteOpen, setPaletteOpen] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen(true);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  return (
    <header className="no-print sticky top-0 z-30 flex h-16 items-center gap-4 border-b border-line bg-surface px-7">
      {showMenuButton ? (
        <button
          type="button"
          onClick={onOpenMenu}
          aria-label={t('panel:nav.openMenu')}
          className="grid h-10 w-10 flex-none place-items-center rounded-md border border-line2 text-text hover:border-accent hover:text-accent"
        >
          <List size={18} />
        </button>
      ) : null}
      <button
        type="button"
        onClick={() => setPaletteOpen(true)}
        aria-keyshortcuts="Control+K Meta+K"
        data-testid="search-trigger"
        className="flex h-10 max-w-[480px] min-w-0 flex-1 items-center gap-2.5 rounded-md border border-line bg-bg2 px-3 text-left text-muted hover:border-line2"
      >
        <MagnifyingGlass size={17} />
        <span className="flex-1 truncate text-sm">{t('panel:shell.search.placeholder')}</span>
        {wide ? (
          <span className="flex-none rounded-[5px] border border-line2 px-1.5 py-0.5 text-[11px] font-medium">
            {t('panel:shell.search.shortcut')}
          </span>
        ) : null}
      </button>
      <div className="ml-auto flex items-center gap-2">
        <SyncPill wide={wide} />
        <LanguageMenu persistRemote />
        <ThemeToggleButton theme={theme} onToggle={toggle} />
        <NotificationsPopover canSettings={canSettings} />
      </div>
      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
    </header>
  );
}
